import React, { memo, useEffect, useState } from "react";
import mixpanel from "mixpanel-browser";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import UserState from "../../../slices/user/UserState";
import { userSelector } from "../../../slices/user/userSlice";
import XGSErrorMessage from "../../../ui-components/error-message/errorMessage";
import { toast } from "react-toastify";
import { Form, Formik, FormikProps } from "formik";
import XGSFormInput from "../../../ui-components/form/input/xgsFormInput";
import { LabelModes } from "../../../ui-components/molecules/labeled-inputs/labeledInput";
import Button, { ButtonThemes } from "../../../ui-components/button/button";
import LabeledDateRangeInput from "../../../ui-components/molecules/labeled-inputs/labeled-date-range-input/labeledDateRangeInput";
import moment from "moment";
import dateFormats from "../../../app/data/common/dateFormats";
import Table from "../../../ui-components/table/table";
import { OperationsReviewFiltersSchema, OperationsReviewWorkItem, OperationsReviewWorkItemFilters, OperationsReviewWorkItemRequest, ShipmentWorkItemReviewStatus } from "../../../app/data/shipment-reconciliation/models";
import { ExportOperationsReviewListState, OperationsReviewListState, OperationsReviewSummaryState } from "../../../slices/shipment-reconciliation/ShipmentReconciliationState";
import { getOperationsReviewListSelector, getOperationsReviewWorkItems, resetList } from "../../../slices/shipment-reconciliation/operations-review/getOperationsReviewListSlice";
import { getOperationsReviewSummaryItems, getOperationsReviewSummarySelector } from "../../../slices/shipment-reconciliation/operations-review/getOperationsReviewSummarySlice";
import { getOperationsReviewListColumns } from "./operationsReviewListColumns";
import "./operationsReview.scss";
import { OperationsReviewSummary } from "./summary/operationReviewSummary";
import OperationsReviewItemDetailsV2 from "./details/operationReviewItemDetailsV2";
import DownloadButton from "ui-components/download-button/downloadButton";
import { downloadOperationsReviewCsv, exportOperationsReviewListSelector } from "slices/shipment-reconciliation/operations-review/exportOperationsReviewListSlice";

const initialFormValues = {
  probillNumber: "",
  etaStartDate: "",
  etaEndDate: "",
  shipperName: "",
  consigneeName: ""
};

const OperationsReview: React.FC<{}> = memo(() => {
  const dispatch = useDispatch();
  const [selectedShipmentWorkItemDetails, setSelectedShipmentWorkItemDetails] = useState<OperationsReviewWorkItem | null>(null);
  const [filterProbillNumber, setFilterProbillNumber] = useState<string | null | undefined>(null);
  const [filterEtaStartDate, setFilterEtaStartDate] = useState<string | null | undefined>(null);
  const [filterEtaEndDate, setFilterEtaEndDate] = useState<string | null | undefined>(null);
  const [filterShipper, setFilterShipper] = useState<string | null | undefined>(null);
  const [filterConsignee, setFilterConsignee] = useState<string | null | undefined>(null);
  const [operationsReviewStatus, setOperationsReviewStatus] = useState<string | null | undefined>(ShipmentWorkItemReviewStatus.PENDING);

  const formRef = React.useRef<FormikProps<OperationsReviewWorkItemFilters>>(null);

  const userState: UserState = useSelector(
    userSelector
  );
  const getOperationsReviewList: OperationsReviewListState = useSelector(
    getOperationsReviewListSelector
  );
  const getOperationsReviewSummary: OperationsReviewSummaryState = useSelector(
    getOperationsReviewSummarySelector
  );
  const exportOperationsReviewListState: ExportOperationsReviewListState = useSelector(
    exportOperationsReviewListSelector
  );

  useEffect(() => {
    mixpanel.track("page_viewed", { page: "shipment_recon_operations" });
    const userTerminal = userState.activeTerminal?.id;
    onTerminalChanged(userTerminal);
    // eslint-disable-next-line
  }, [userState.activeTerminal?.id])

  const onTerminalChanged = (userTerminal: number | null | undefined) => {
    dispatch(resetList());
    if (!userTerminal) return; // can not get the Probills without knowing the terminal
    formRef.current?.submitForm();
  };

  const showShipmentWorkItemDetails = (shipmentWorkItem: OperationsReviewWorkItem) => {
    setSelectedShipmentWorkItemDetails(shipmentWorkItem);
  }

  const getFiltersRequest = (
    terminalNumber: number,
    probillNumber: number | string | null | undefined,
    etaStartDate: string | null | undefined,
    etaEndDate: string | null | undefined,
    shipperName?: string | null | undefined,
    consigneeName?: string | null | undefined,
    operationsReviewStatus?: string | null | undefined,
  ): OperationsReviewWorkItemRequest => {
    let request: OperationsReviewWorkItemRequest = {
      terminalNumber
    };
    if (probillNumber) request.probillNumber = probillNumber;
    if (etaStartDate) request.etaStartDate = moment(etaStartDate).format(dateFormats.isoDate);
    if (etaEndDate) request.etaEndDate = moment(etaEndDate).format(dateFormats.isoDate);
    if (shipperName) request.shipperName = shipperName;
    if (consigneeName) request.consigneeName = consigneeName;
    if (operationsReviewStatus) request.operationsReviewStatus = operationsReviewStatus;
    return request;
  }

  const onSubmit = (data: OperationsReviewWorkItemFilters) => {
    if (!userState.activeTerminal?.id) return;
    let requestData: OperationsReviewWorkItemRequest = getFiltersRequest(
      userState.activeTerminal?.id,
      data.probillNumber,
      data.etaStartDate,
      data.etaEndDate,
      data.shipperName,
      data.consigneeName
    );
    dispatch(getOperationsReviewSummaryItems(
      {...requestData},
      (error) => { toast.error(error) }
    ));
    if(operationsReviewStatus) requestData.operationsReviewStatus = operationsReviewStatus;
    dispatch(getOperationsReviewWorkItems(
      {...requestData},
      (error) => { toast.error(error) }
    ));
  };

  const onListInfiniteScroll = () => {
    if (!userState.activeTerminal?.id) return;
    if (!getOperationsReviewList.hasNext) return;
    let requestData: OperationsReviewWorkItemRequest = getFiltersRequest(
      userState.activeTerminal?.id,
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
      operationsReviewStatus
    );
    requestData.cursor = getOperationsReviewList.nextCursor;
    dispatch(getOperationsReviewWorkItems(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  const onSummaryApply = (operationsReviewStatus: ShipmentWorkItemReviewStatus) => {
    if (!userState.activeTerminal?.id) return;
    if (!operationsReviewStatus) return;
    setOperationsReviewStatus(operationsReviewStatus);
    let requestData: OperationsReviewWorkItemRequest = getFiltersRequest(
      userState.activeTerminal?.id,
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
    );
    requestData.operationsReviewStatus = operationsReviewStatus;
    dispatch(getOperationsReviewWorkItems(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  const onClearFilters = ()=>{
    setFilterProbillNumber(null);
    setFilterEtaStartDate(null);
    setFilterEtaEndDate(null);
    setFilterShipper(null);
    setFilterConsignee(null);
    setOperationsReviewStatus(null);
    formRef.current?.resetForm();
  }

  const showFiltersNotification = () =>{
    return !!(
      filterProbillNumber||
      filterEtaStartDate ||
      filterEtaEndDate ||
      filterShipper ||
      filterConsignee ||
      operationsReviewStatus 
    );
  }

  const exportOperationsReviewList = () =>{
    if (!userState.activeTerminal?.id) return;
    let requestData: OperationsReviewWorkItemRequest = getFiltersRequest(
      userState.activeTerminal?.id,
      filterProbillNumber,
      filterEtaStartDate,
      filterEtaEndDate,
      filterShipper,
      filterConsignee,
      operationsReviewStatus
    );
    dispatch(downloadOperationsReviewCsv(
      requestData,
      (error) => { toast.error(error) }
    ));
  }

  return (
    <ContentContainer
      className="xgs-list xgs-list--full-width"
      mods={{ 'full-width': true }}
      titleComponent={
        <ContentContainerToolbar
          title="Operations Review"
          mods={{ "full-width": true }}
        >
          <DownloadButton
           title="Export CSV"
           spinner={exportOperationsReviewListState.loading}
           disabled={exportOperationsReviewListState.loading}
           onClick={() => exportOperationsReviewList()}
       />
        </ContentContainerToolbar>
      }
    >
      <div className="xgs-operations-review">
        <div className="xgs-list__controls">
          <div className="xgs-list__controls__search">
            <Formik
              initialValues={initialFormValues}
              onSubmit={onSubmit}
              validationSchema={OperationsReviewFiltersSchema}
              innerRef={formRef}
            >
              {(props: FormikProps<OperationsReviewWorkItemFilters>) => (
                <Form className="xgs-list__controls__search-form">
                  <XGSFormInput
                    type="text"
                    name="probillNumber"
                    label="Probill"
                    placeholder="Probill Number"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("probillNumber", e?.currentTarget?.value || undefined);
                      setFilterProbillNumber(e?.currentTarget?.value);
                    }
                    }
                  />
                  <LabeledDateRangeInput
                    label="ETA Range"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    start={props.values.etaStartDate}
                    end={props.values.etaEndDate}
                    onStartChange={(v) => {
                      props.setFieldValue("etaStartDate", v || undefined);
                      setFilterEtaStartDate(v || null);
                    }}
                    onEndChange={(v) => {
                      props.setFieldValue("etaEndDate", v || undefined);
                      setFilterEtaEndDate(v || null);
                    }}
                  />
                  <XGSFormInput
                    type="text"
                    name="shipperName"
                    label="Shipper"
                    placeholder="Search Shipper Name"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("shipperName", e.currentTarget.value);
                      setFilterShipper(e.currentTarget.value);
                    }
                    }
                  />
                  <XGSFormInput
                    type="text"
                    name="consigneeName"
                    label="Consignee"
                    placeholder="Search Consignee Name"
                    labelMode={LabelModes.column}
                    className="xgs-item-form__field"
                    onChange={(e) => {
                      props.setFieldValue("consigneeName", e.currentTarget.value);
                      setFilterConsignee(e.currentTarget.value);
                    }
                    }
                  />
                  <Button
                    type="submit"
                    theme={ButtonThemes.blue}
                    spinner={false}
                    disabled={getOperationsReviewList.requestStarted || getOperationsReviewList.fetchPortionStarted}
                  >
                    Search
                  </Button>
                  <Button
                    theme={ButtonThemes.gray}
                    spinner={false}
                    disabled={getOperationsReviewList.requestStarted || getOperationsReviewList.fetchPortionStarted}
                    onClick={onClearFilters}
                  >
                    Clear
                  </Button>
                </Form>
              )}
            </Formik>
          </div>
        </div>
        <div className="xgs-operations-review__summary">
          <OperationsReviewSummary
            summaryData={getOperationsReviewSummary.summary}
            isLoading={getOperationsReviewSummary.loading}
            onApply={onSummaryApply}
            isFiltersApplied={showFiltersNotification()}
          />
        </div>
        {(!userState.activeTerminal?.id) ? (
          <XGSErrorMessage>
            <div>
              You do not have assigned terminals.
              Email <a className="white-link" href="mailto:helpdesk@xgsi.com">helpdesk@xgsi.com</a> to request terminal assignment for your account.
            </div>
          </XGSErrorMessage>
        ) : (
          <div className="xgs-operations-review__table-wrapper">
            <Table
              isLoading={getOperationsReviewList.requestStarted}
              columns={getOperationsReviewListColumns()}
              data={getOperationsReviewList.shipmentWorkItems}
              cursorPointer={true}
              rowHeight={64}
              noResultsText="No Shipments available"
              onRowClicked={(row: OperationsReviewWorkItem) => { showShipmentWorkItemDetails(row) }}
              onSelectedRowsChange={null}
              infiniteScroll
              infiniteScrollLoading={getOperationsReviewList.fetchPortionStarted}
              infiniteScrollHasNext={getOperationsReviewList.hasNext}
              onInfiniteScroll={onListInfiniteScroll}
              responsive={true}
              minTableHeight={400}
            />
          </div>
        )}
        {(selectedShipmentWorkItemDetails && selectedShipmentWorkItemDetails.id) && (
          <OperationsReviewItemDetailsV2
            shipmentWorkItemId={selectedShipmentWorkItemDetails.id}
            probillNumber={+selectedShipmentWorkItemDetails.probillNumber}
            terminalAirportCode={selectedShipmentWorkItemDetails.terminalAirportcode}
            terminalName={selectedShipmentWorkItemDetails.terminalName}
            terminalNumber={selectedShipmentWorkItemDetails.terminalNumber}
            onClose={() => {
              setSelectedShipmentWorkItemDetails(null);
            }}
          />
        )}
      </div>
    </ContentContainer>
  );
});

export default OperationsReview;
